import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IText, TGetBookingResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { Yii } from 'libraries/interfaces/Yii';
import { CardComponent } from 'libraries/ui_card/card.component';
import { IPhone } from 'libraries/service/resources/model/booking/IAgency';
import * as moment from "moment";
import { DateService } from "libraries/date/date.service";
import { faPhone, faEnvelope, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { EExternalBookingDisplayMode } from "libraries/component/external-booking/external-booking.component";

interface IBookingTranslated {
    agentName: string;
    agencyPhoneNumber: string;
    agentEmail: string;
    whenLastUpdated: string;
}

@Component({
    selector: 'identification',
    templateUrl: './booking-technical-informations.component.html',
    styles: [`
        .card-body-content {
            display: flex;
            padding: .75rem;
            flex-wrap: wrap;
            padding-bottom: 0;
        }

        .minor-info {
            display: block;
            font-size: 0.9rem;
            margin-top: 0.2rem;
        }

        .column {
            flex: 1;
            margin-bottom: .75rem;
        }

        .nobreak {
            white-space: nowrap;
        }

        .card-header-content .title {
            cursor: pointer;
        }

        .icon {
            width: 20px;
        }

        .contact-content {
            padding-left: 20px;
        }

        .label-name {
            width: 60px;
        }

    `]
})
export class BookingTechnicalInformationsComponent implements OnChanges {

    public bookingTranslated: IBookingTranslated;

    public labels: { [key: string]: string } = {
        title: Yii.t('wui-shared-js', 'Identification'),
        version: Yii.t('wui-shared-js', 'Version'),
        source: Yii.t('wui-shared-js', 'source'),
        createdDate: Yii.t('wui-shared-js', 'Créé le'),
        lastUpdateDate: Yii.t('wui-shared-js', 'Dernière mise à jour le'),
        agency: Yii.t('wui-shared-js', 'Agence'),
        agent: Yii.t('wui-shared-js', 'Agent'),
        contact: Yii.t('wui-shared-js', 'Contact'),
        reference: Yii.t('wui-shared-js', 'Réf.'),
    };

    public name: string = 'bookingTechnicalInformations';

    @Input()
    public bookingData: TGetBookingResponse;

    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    public iconPhone: IconDefinition = faPhone;
    public iconEnvelope: IconDefinition = faEnvelope;

    public displayModes: typeof EExternalBookingDisplayMode = EExternalBookingDisplayMode;

    constructor(private readonly dateService: DateService) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const currentBookingData: TGetBookingResponse = changes.bookingData.currentValue;
        if (currentBookingData) {
            this.init(currentBookingData);
        }
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.name);
    }

    private init(currentBookingData: TGetBookingResponse): void {
        this.bookingTranslated = {
            agentName: this.getAgentName(currentBookingData),
            agencyPhoneNumber: this.getAgencyPhoneNumber(currentBookingData),
            agentEmail: this.getAgentEmail(currentBookingData),
            whenLastUpdated: this.getWhenLastUpdated(currentBookingData)
        };
    }

    private getAgentName(currentBookingData: TGetBookingResponse): string {
        return currentBookingData.agent ? `${currentBookingData.agent.firstName || ''} ${currentBookingData.agent.lastName || ''}` : undefined;
    }

    private getAgencyPhoneNumber(currentBookingData: TGetBookingResponse): string {
        const phoneNumbers: string = currentBookingData.agency && currentBookingData.agency.telephone ?
            currentBookingData.agency.telephone
                .filter((phone: IPhone) => phone.number)
                .map((phone: IPhone) => phone.number.toString())
                .join(', ') : undefined;
        return phoneNumbers ? `${phoneNumbers}` : undefined;
    }

    private getAgentEmail(currentBookingData: TGetBookingResponse): string {
        const emails: string = currentBookingData.agent && currentBookingData.agent.email ? currentBookingData.agent.email.map(
            (phone: IText) => phone.text
        ).join(', ') : undefined;
        return emails ? `${emails}` : undefined;
    }

    private getWhenLastUpdated(currentBookingData: TGetBookingResponse): string {
        if (!currentBookingData.whenLastUpdated) {
            return undefined;
        }

        const format: string = currentBookingData.whenLastUpdated.indexOf('T') === -1 ?
            this.dateService.dateFormats.momentMedium :
            `${this.dateService.dateFormats.momentMedium} - ${this.dateService.dateFormats.momentTime} Z`;
        return moment.parseZone(currentBookingData.whenLastUpdated).format(format);
    }
}
